import React, { useEffect, useRef, useState } from 'react';
import { naoPerturbeAPI } from '@/lib/api';
import { PhoneOff, PhoneCall, HelpCircle, Loader2, RefreshCw, ShieldAlert, ShieldCheck } from 'lucide-react';

const fmtDataHora = (iso) => (iso ? new Date(iso).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '');
const soDigitos = (v) => String(v || '').replace(/\D/g, '');

// Selo ao lado do telefone: BQ (vermelho) / LB (verde) / não consultado (cinza)
export const NaoPerturbeBadge = ({ info, consultadoEm, size = 'sm', testid }) => {
  if (!info || !info.codigo) {
    return (
      <span className={`inline-flex items-center gap-1 rounded-md border border-dashed border-gray-300 dark:border-gray-600 px-1.5 py-0.5 text-[10px] font-medium text-gray-400`} title="Lei Não Perturbe: ainda não consultado" data-testid={testid}>
        <HelpCircle className="w-3 h-3" /> LNP
      </span>
    );
  }
  const bloqueado = info.bloqueado;
  const semRetorno = info.codigo === 'SEM_RETORNO' || info.codigo === 'DESCONHECIDO';
  const cls = bloqueado
    ? 'bg-red-600 text-white border-red-700 shadow-sm shadow-red-900/30'
    : semRetorno
      ? 'bg-gray-200 text-gray-700 border-gray-300 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600'
      : 'bg-emerald-600 text-white border-emerald-700';
  const sigla = bloqueado ? 'BQ' : semRetorno ? '--' : 'LB';
  const texto = bloqueado ? 'Não Perturbe' : semRetorno ? 'Sem retorno' : 'Liberado';
  const Icone = bloqueado ? PhoneOff : semRetorno ? HelpCircle : PhoneCall;
  const pad = size === 'md' ? 'px-2 py-1 text-xs' : 'px-1.5 py-0.5 text-[10px]';
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-md border font-bold tracking-wide ${pad} ${cls}`}
      title={`Lei Não Perturbe: ${info.descricao || texto}${consultadoEm ? ` • consultado em ${fmtDataHora(consultadoEm)}` : ''}`}
      data-testid={testid}
      data-lnp={sigla}
    >
      <Icone className="w-3 h-3" />
      <span>{sigla}</span>
      <span className="font-medium opacity-90">{texto}</span>
    </span>
  );
};

// Consulta ao vivo enquanto o vendedor digita (Nova Venda)
export const NaoPerturbeInline = ({ telefone, testid }) => {
  const [estado, setEstado] = useState({ numero: null, info: null, status: null, carregando: false });
  const cache = useRef({});
  const digitos = soDigitos(telefone);
  const numero = digitos.length === 13 && digitos.startsWith('55') ? digitos.slice(2) : digitos;
  const valido = numero.length === 10 || numero.length === 11;

  useEffect(() => {
    if (!valido) { setEstado({ numero: null, info: null, status: null, carregando: false }); return; }
    if (cache.current[numero]) { setEstado({ numero, carregando: false, ...cache.current[numero] }); return; }
    let ativo = true;
    setEstado((e) => ({ ...e, numero, carregando: true }));
    const t = setTimeout(async () => {
      try {
        const res = await naoPerturbeAPI.consultar([numero]);
        const item = (res.data.telefones || [])[0] || null;
        const r = { info: item && item.codigo ? item : null, status: res.data.status };
        cache.current[numero] = r;
        if (ativo) setEstado({ numero, carregando: false, ...r });
      } catch {
        if (ativo) setEstado({ numero, carregando: false, info: null, status: 'erro' });
      }
    }, 600);
    return () => { ativo = false; clearTimeout(t); };
  }, [numero, valido]);

  if (!valido) return null;
  if (estado.carregando) {
    return <span className="mt-1 inline-flex items-center gap-1 text-[11px] text-gray-400" data-testid={testid}><Loader2 className="w-3 h-3 animate-spin" /> Consultando Lei Não Perturbe…</span>;
  }
  if (estado.status && estado.status !== 'ok') {
    return <span className="mt-1 inline-flex items-center gap-1 text-[11px] text-amber-600 dark:text-amber-400" data-testid={testid} title="Integração Conexão Claro indisponível no momento"><HelpCircle className="w-3 h-3" /> LNP não consultado</span>;
  }
  return (
    <div className="mt-1 flex items-center gap-2" data-testid={testid}>
      <NaoPerturbeBadge info={estado.info} />
      {estado.info?.bloqueado && <span className="text-[11px] font-medium text-red-600 dark:text-red-400">Cliente na lista Não Perturbe — atenção ao contato.</span>}
    </div>
  );
};

// Resumo no Detalhar venda
export const NaoPerturbeResumo = ({ resultado, onReconsultar, carregando }) => {
  const ok = resultado?.status === 'ok';
  const bloqueados = ok ? resultado.bloqueados : 0;
  const cor = !resultado ? 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50'
    : !ok ? 'border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20'
      : bloqueados ? 'border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-900/20' : 'border-emerald-300 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/20';
  const Icone = bloqueados ? ShieldAlert : ShieldCheck;
  const titulo = !resultado ? 'Lei Não Perturbe: ainda não consultado'
    : !ok ? `Lei Não Perturbe: ${resultado.mensagem || 'não foi possível consultar'}`
      : bloqueados ? `Lei Não Perturbe: ${bloqueados} de ${resultado.total} telefone(s) BLOQUEADO(S)` : `Lei Não Perturbe: ${resultado.total} telefone(s) liberado(s)`;
  return (
    <div className={`flex flex-wrap items-center justify-between gap-2 rounded-lg border px-3 py-2 ${cor}`} data-testid="nao-perturbe-resumo">
      <div className="flex items-center gap-2 min-w-0">
        <Icone className={`w-4 h-4 shrink-0 ${bloqueados ? 'text-red-600' : ok ? 'text-emerald-600' : 'text-amber-600'}`} />
        <div className="min-w-0">
          <p className={`text-sm font-semibold ${bloqueados ? 'text-red-700 dark:text-red-300' : ''}`} data-testid="nao-perturbe-titulo">{titulo}</p>
          {resultado?.consultado_em && <p className="text-[11px] text-gray-500">Consultado em {fmtDataHora(resultado.consultado_em)}{resultado.consultado_por ? ` por ${resultado.consultado_por}` : ''}</p>}
        </div>
      </div>
      {onReconsultar && (
        <button onClick={onReconsultar} disabled={carregando} className="inline-flex items-center gap-1 rounded-md border bg-white dark:bg-gray-900 px-2.5 py-1 text-xs font-medium hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-60" data-testid="nao-perturbe-reconsultar">
          {carregando ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Reconsultar
        </button>
      )}
    </div>
  );
};

export const lnpDoCampo = (resultado, campo) => {
  if (!resultado || resultado.status !== 'ok') return null;
  return (resultado.telefones || []).find((t) => t.campo === campo) || null;
};
